import { generateDeck, type GameCard } from "../shared/deck";
import type { CardCollection } from "../shared/collections";
import { createCard } from "./card";
import { stack } from "./ui";

/** A short Puits round: five personal cards are placed onto one center card. */
const deck = generateDeck(7);
const builtInSequence = [[0, 2], [2, 35], [35, 11], [11, 27], [27, 30]].map(
  ([first, second]) => deck.find(card => card.symbols.includes(first) && card.symbols.includes(second))!,
);
builtInSequence.push(deck[0]);

export interface HomeDemo {
  setActive(active: boolean): void;
  destroy(): void;
}

export function createHomeDemo(root: HTMLElement, collection?: CardCollection | null): HomeDemo {
  const sequence = collection ? generateDeck(collection.order).slice(0, 6) : builtInSequence;
  const handSize = sequence.length - 1;
  const draw = root.querySelector<HTMLElement>(".home-demo-draw .home-demo-cards")!;
  const won = root.querySelector<HTMLElement>(".home-demo-won .home-demo-cards")!;
  const remaining = root.querySelector<HTMLElement>(".home-demo-remaining")!;
  const total = root.querySelector<HTMLElement>(".home-demo-total")!;
  const point = root.querySelector<HTMLElement>(".home-demo-point")!;
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  const animations = new Set<Animation>();
  let timer = 0;
  let active = false;
  let running = false;
  let score = 0;
  let flight: HTMLElement | null = null;

  const ownCard = () => sequence[score + 1] ?? null;
  const centerCard = () => sequence[score];

  function face(host: HTMLElement, card: GameCard | null, count: number, variant: string) {
    host.innerHTML = stack("", count, variant);
    const slot = host.querySelector<HTMLElement>(".card-face")!;
    slot.removeAttribute("id");
    host.classList.toggle("home-demo-empty", !card);
    if (card) slot.append(createCard(card, { collection }));
  }

  function renderBoard() {
    face(draw, centerCard(), score, "center");
    face(won, ownCard(), Math.max(0, handSize - score - 1), "own");
    remaining.textContent = String(score + 1);
    total.textContent = String(handSize - score);
    root.dataset.phase = "search";
  }

  function animate(node: HTMLElement, frames: Keyframe[], duration: number, easing = "ease-out") {
    const animation = node.animate(frames, { duration, easing });
    animations.add(animation);
    animation.onfinish = () => animations.delete(animation);
  }

  function later(ms: number, next: () => void) {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (running && root.isConnected) next();
    }, ms);
  }

  function markMatch() {
    const common = ownCard()!.symbols.find(id => centerCard().symbols.includes(id))!;
    for (const symbol of root.querySelectorAll<HTMLElement>(`.card-symbol[data-symbol-id="${common}"]`))
      symbol.classList.add("home-demo-match");
  }

  function find() {
    root.dataset.phase = "match";
    markMatch();
    later(650, transfer);
  }

  function transfer() {
    root.dataset.phase = "transfer";
    const card = ownCard()!;
    const from = won.querySelector<HTMLElement>(".card-face")!.getBoundingClientRect();
    const to = draw.querySelector<HTMLElement>(".card-face")!.getBoundingClientRect();
    const bounds = root.getBoundingClientRect();
    const dx = to.left - from.left;
    const dy = to.top - from.top - 1;
    flight = createCard(card, { collection });
    flight.classList.add("home-demo-flight");
    Object.assign(flight.style, {
      left: `${from.left - bounds.left}px`,
      top: `${from.top - bounds.top}px`,
      width: `${from.width}px`,
    });
    root.append(flight);
    // Reveal the next personal card while the previous one joins the center.
    face(won, sequence[score + 2] ?? null, Math.max(0, handSize - score - 2), "own");
    total.textContent = String(handSize - score - 1);
    for (const symbol of draw.querySelectorAll(".home-demo-match")) symbol.classList.remove("home-demo-match");
    animate(flight, [
      { transform: "translate(0,0) rotate(6deg)" },
      { transform: `translate(${dx * .5}px,${dy * .5 - 34}px) rotate(0deg) scale(1.07)`, offset: .5 },
      { transform: `translate(${dx}px,${dy}px) rotate(-6deg)` },
    ], 700, "cubic-bezier(.3,.05,.25,1)");
    later(700, () => land(card));
  }

  function land(card: GameCard) {
    score++;
    face(draw, card, score, "center");
    flight?.remove();
    flight = null;
    remaining.textContent = String(score + 1);
    root.dataset.phase = "score";
    animate(draw, [
      { transform: "translateY(0)" },
      { transform: "translateY(5px)", offset: .35 },
      { transform: "translateY(0)" },
    ], 260);
    animate(point, [
      { opacity: 0, transform: "translateY(10px) scale(.7)" },
      { opacity: 1, transform: "translateY(-8px) scale(1)", offset: .25 },
      { opacity: 1, transform: "translateY(-15px) scale(1)", offset: .65 },
      { opacity: 0, transform: "translateY(-24px) scale(1)" },
    ], 950);
    if (score === handSize) later(1300, reset);
    else later(1450, find);
  }

  function reset() {
    root.dataset.phase = "deal";
    animate(draw, [{ opacity: 0, transform: "translateY(-14px)" }, { opacity: 1, transform: "translateY(0)" }], 360);
    animate(won, [{ opacity: 0 }, { opacity: 1 }], 360);
    score = 0;
    renderBoard();
    later(1700, find);
  }

  function stop() {
    clearTimeout(timer);
    running = false;
    for (const animation of animations) animation.cancel();
    animations.clear();
    flight?.remove();
    flight = null;
    score = 0;
    renderBoard();
  }

  function sync() {
    const shouldRun = active && !document.hidden && !motion.matches;
    if (shouldRun === running) return;
    stop();
    if (shouldRun) {
      running = true;
      later(1700, find);
    } else if (active && motion.matches) markMatch();
  }

  renderBoard();
  document.addEventListener("visibilitychange", sync);
  motion.addEventListener("change", sync);
  return {
    setActive(value) { active = value; sync(); },
    destroy() {
      active = false;
      stop();
      document.removeEventListener("visibilitychange", sync);
      motion.removeEventListener("change", sync);
    },
  };
}
